import React from "react";
import { Link } from "react-router-dom";
import { Instagram, Linkedin, Facebook, Mail, MapPin, ArrowUpRight, Cookie, MessageCircle } from "lucide-react";
import { services } from "@/data/services";
import { useCookieConsent } from "@/hooks/useCookieConsent";
import { Button, Assurances } from "@/components/kit";

const LOGO = "/brand/uppit-logo-creme.png";
const WHATSAPP =
  "https://wa.me/31641262267?text=" +
  encodeURIComponent("Hoi Tim, ik wil graag samenwerken met UPPIT.");

const Footer = () => {
  const { reset: openCookieSettings } = useCookieConsent();

  return (
    <footer data-testid="main-footer" className="relative bg-obsidian text-ivory overflow-hidden">
      {/* Pre-footer CTA */}
      <div className="relative border-b border-white/10">
        <div className="glow-orb w-[560px] h-[560px] bg-electric/20 -top-40 left-1/2 -translate-x-1/2" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-28 text-center">
          <div className="font-mono text-[11px] uppercase tracking-[0.28em] text-electric-300 mb-6">
            Klaar om te groeien?
          </div>
          <h2 className="font-display font-black tracking-tight leading-[0.95] text-4xl sm:text-5xl lg:text-7xl text-ivory max-w-4xl mx-auto text-balance">
            Laten we jouw merk online <span className="text-gradient-blue">onmisbaar</span> maken.
          </h2>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Button to="/ai-scan" variant="primary" testid="footer-cta-aiscan" className="px-8 py-4">
              Start gratis AI Growth Scan
            </Button>
            <Button href={WHATSAPP} target="_blank" rel="noopener noreferrer" variant="outlineLight" testid="footer-cta-whatsapp" className="px-8 py-4">
              <MessageCircle className="w-4 h-4" /> Plan een gesprek
            </Button>
          </div>
          <Assurances testid="footer-assurances" dark className="mt-8 justify-center" />
        </div>
      </div>

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 pt-16 pb-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
          <div className="lg:col-span-4">
            <Link to="/" aria-label="UPPIT home" className="inline-flex">
              <img src={LOGO} alt="UPPIT logo" width="150" height="50" className="h-11 w-auto select-none" draggable="false" />
            </Link>
            <p className="mt-5 text-white/55 max-w-sm leading-relaxed">
              Online marketingbureau voor MKB &amp; ZZP. AI-gedreven groei, eerlijke aanpak, meetbare resultaten.
            </p>
            <div className="flex items-center gap-3 mt-6">
              <SocialLink href="https://www.instagram.com/uppitgrowth/" Icon={Instagram} label="Instagram" testid="social-instagram" />
              <SocialLink href="https://www.linkedin.com/in/eigenwijzetim/" Icon={Linkedin} label="LinkedIn" testid="social-linkedin" />
              <SocialLink href="https://www.facebook.com/" Icon={Facebook} label="Facebook" testid="social-facebook" />
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-white/40 mb-4">Diensten</div>
            <ul className="space-y-2.5 text-sm text-white/70">
              {services.map((s) => (
                <li key={s.slug}>
                  <Link
                    to={`/diensten/${s.slug}`}
                    data-testid={`footer-link-${s.slug}`}
                    className="hover:text-electric-300 transition-colors inline-flex items-center gap-1.5 group"
                  >
                    {s.title}
                    <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-2">
            <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-white/40 mb-4">Bedrijf</div>
            <ul className="space-y-2.5 text-sm text-white/70">
              <li><Link to="/over-ons" className="hover:text-electric-300 transition-colors">Over ons</Link></li>
              <li><Link to="/cases" className="hover:text-electric-300 transition-colors">Cases</Link></li>
              <li><Link to="/blog" data-testid="footer-blog" className="hover:text-electric-300 transition-colors">Kennisbank</Link></li>
              <li><Link to="/ai-scan" className="hover:text-electric-300 transition-colors">AI Scan</Link></li>
              <li><Link to="/contact" className="hover:text-electric-300 transition-colors">Contact</Link></li>
              <li><Link to="/sitemap" data-testid="footer-sitemap" className="hover:text-electric-300 transition-colors">Sitemap</Link></li>
            </ul>
          </div>

          <div className="lg:col-span-3">
            <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-white/40 mb-4">Contact</div>
            <ul className="space-y-3 text-sm text-white/70">
              <li className="flex items-start gap-2.5">
                <Mail className="w-4 h-4 text-electric-300 mt-0.5 shrink-0" />
                <a href="mailto:info@uppit.nl" className="hover:text-electric-300 transition-colors">info@uppit.nl</a>
              </li>
              <li className="flex items-start gap-2.5">
                <MessageCircle className="w-4 h-4 text-electric-300 mt-0.5 shrink-0" />
                <a href={WHATSAPP} target="_blank" rel="noopener noreferrer" className="hover:text-electric-300 transition-colors">+31 6 41 26 22 67</a>
              </li>
              <li className="flex items-start gap-2.5">
                <MapPin className="w-4 h-4 text-electric-300 mt-0.5 shrink-0" />
                <span>Gevestigd in Nederland</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-xs text-white/40">
            © {new Date().getFullYear()} UPPIT — Alle rechten voorbehouden.
          </div>
          <div className="text-xs text-white/40 flex items-center gap-4 flex-wrap justify-center">
            <span>KVK ingeschreven</span>
            <span className="w-1 h-1 rounded-full bg-white/25" />
            <Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-electric-300 transition-colors">
              Privacybeleid
            </Link>
            <span className="w-1 h-1 rounded-full bg-white/25" />
            <Link to="/cookies" data-testid="footer-cookies-link" className="hover:text-electric-300 transition-colors">
              Cookiebeleid
            </Link>
            <span className="w-1 h-1 rounded-full bg-white/25" />
            <button
              type="button"
              onClick={openCookieSettings}
              data-testid="footer-cookie-settings"
              className="inline-flex items-center gap-1.5 hover:text-electric-300 transition-colors"
            >
              <Cookie className="w-3 h-3" /> Cookie-instellingen
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

const SocialLink = ({ href, Icon, label, testid }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    aria-label={label}
    data-testid={testid}
    className="w-10 h-10 grid place-items-center rounded-full border border-white/15 text-white/60 hover:text-white hover:bg-electric hover:border-electric transition-all"
  >
    <Icon className="w-4 h-4" />
  </a>
);

export default Footer;
